// #2142 (docs/score/announcer-ui-redesign.md §4.4/§5) — band 4, the look-up
// column's pure selection logic. The column shows exactly one class, driven
// by AnnouncerContext's shared `viewingClassId` (null = "following the
// ring", set by #2140's day spine on a class-row click and read here — this
// card adds NO second pinned-class concept).
import { RIDE_STATUSES, dropFvdPlaceholderTestName } from "score-common";
import { isResultRevealed } from "./orderOfGo.js";
import { findActiveClassLabel } from "./liveDesk.js";

// STAY PINNED (§5): the viewed class is whichever the announcer explicitly
// clicked, or the running class by default. "Following" means the column is
// currently showing whatever IS the running class right now — true both for
// the untouched null-viewingClassId default AND for an explicit click that
// happens to land back on the running row (day-spine's RUNNING row, AC10).
export function isFollowingRing(viewedClassId, liveClassId) {
  return viewedClassId === liveClassId;
}

// §5 "a dot on the button" — something landed while the announcer was away:
// the ring moved on to a different running class than the one that was
// running when they pinned away, OR that same running class's placings were
// committed while they were away. Only meaningful while NOT following (the
// dot lives on the return button, which only renders in that state).
export function computeReturnDot({
  following,
  liveClassId,
  liveClassIdWhenLeft,
  placedWhenLeft,
  placedNow,
}) {
  if (following) return false;
  if (liveClassId !== liveClassIdWhenLeft) return true;
  return Boolean(placedNow) && !placedWhenLeft;
}

// §4.4 "every ride ... with scores": not-yet-ridden shows a dash, the ride
// in the ring shows "pending", a SCRATCHED ride shows no score at all (its
// exception label carries the termReason instead, see LookupColumn.jsx's
// own rideExceptionLabel, same precedent as LiveDesk.jsx's #1838 pattern).
// A ride that has already run but whose own result isn't CONFIRMED/FINAL
// yet also reads "pending" — never leak an unrevealed score (#699).
export const RIDE_LOOKUP_KIND = {
  SCRATCHED: "SCRATCHED",
  DASH: "DASH",
  PENDING: "PENDING",
  SCORE: "SCORE",
};

// #2584 (TRI-237) — the look-up column's panel had no heading naming which
// class/test is being viewed at all (just "Back to the ring"/"RESULTS").
// `classLabel` reuses #2585's findActiveClassLabel (same classes array this
// column already reads via useRingData()) so it renders for EVERY class,
// including one with no rides fetched yet or no results yet — it never
// depends on `rideRows`. `testName` is read off the viewed class's OWN ride
// rows (buildOrderOfGoRows already resolves ride.testId -> test.name onto
// each row, RideRow.jsx's same field) rather than any class-level testId:
// #435/useRoster.js's own comment is explicit that a class's testId is only
// a hint, the per-ride value is authoritative. Takes the first row that
// resolves to a real (non-FVD-placeholder) test name — a class's rides
// normally share one test, and an empty rideRows array (class not reached
// yet) falls through to "" exactly like "no test assigned" (AC3), since
// there is nothing yet to contradict that reading.
export function describeLookupClassHeading({ classes, classId, rideRows }) {
  const classLabel = findActiveClassLabel(classes, classId);
  const testName =
    (rideRows || [])
      .map((row) => dropFvdPlaceholderTestName(row?.testName))
      .find(Boolean) || "";
  return { classLabel, testName };
}

// #2633 — the sponsor/prize shown in the LookupColumn header for the class
// currently IN THE RING. Note this is the OPPOSITE convention to
// describeLookupClassHeading above: the heading names the class being
// VIEWED, while the sponsorship block names the LIVE class, because the
// announcer reads the sponsor credit out for whatever is running right now
// regardless of what they have pinned back to look at (card #2633: "Show it
// for the class currently in the ring"). The one call site,
// LookupColumn.jsx, therefore passes `classId: liveClassId`, not
// `viewedClassId`. score-api's class read already
// resolves sponsorId into an inline `sponsor: {name, textLine}` object
// (apps/score-api/src/handlers/class.js), so this is pure selection, no
// fetch. Each of the three pieces is returned independently (null when
// absent) so the component can render only the parts that exist rather than
// building one composed sentence that would leave dangling punctuation when
// a piece is missing (AC3/AC4).
export function resolveClassSponsorship({ classes, classId }) {
  const cls = (classes || []).find((c) => c?.id === classId);
  const sponsorName = cls?.sponsor?.name || null;
  const sponsorTextLine = cls?.sponsor?.textLine || null;
  const prize = cls?.prize || null;
  return { sponsorName, sponsorTextLine, prize };
}

// #3393 -- the heading's status line. PLACED wins; otherwise a class with any
// scoring activity is IN_PROGRESS (ranks unofficial), else NOT_STARTED (rides
// are in ride order). Whether it renders green is the caller's `following`
// (green means live only), not part of the kind.
export const CLASS_STATUS_KIND = {
  PLACED: "PLACED",
  IN_PROGRESS: "IN_PROGRESS",
  NOT_STARTED: "NOT_STARTED",
};

export function classifyClassStatus({ placed, rideRows, results }) {
  if (placed) return CLASS_STATUS_KIND.PLACED;
  const ridden = (rideRows || []).some(
    (r) =>
      r?.status === RIDE_STATUSES.IN_PROGRESS ||
      r?.status === RIDE_STATUSES.ENTERING_RING ||
      r?.status === RIDE_STATUSES.COMPLETED,
  );
  return ridden || (results || []).length > 0
    ? CLASS_STATUS_KIND.IN_PROGRESS
    : CLASS_STATUS_KIND.NOT_STARTED;
}

export function classifyRideForLookup({ status, isCurrent, result }) {
  if (status === RIDE_STATUSES.SCRATCHED) {
    return { kind: RIDE_LOOKUP_KIND.SCRATCHED, composite: null };
  }
  if (isCurrent) {
    return { kind: RIDE_LOOKUP_KIND.PENDING, composite: null };
  }
  if (status === RIDE_STATUSES.PENDING) {
    return { kind: RIDE_LOOKUP_KIND.DASH, composite: null };
  }
  if (result && isResultRevealed(result.status)) {
    return { kind: RIDE_LOOKUP_KIND.SCORE, composite: result.composite };
  }
  return { kind: RIDE_LOOKUP_KIND.PENDING, composite: null };
}
